import { Show, type JSX } from 'solid-js'

import { Brand } from '../icons'

import Css from './account.module.scss'

/**
 * As pecas das tres telas de conta - entrar, criar e recuperar a senha.
 *
 * Existem para que as tres tenham a MESMA linguagem: mesmo campo, mesmo botao,
 * mesma moldura. Sem isso, quem vem da pagina que vende atravessa tres telas que
 * parecem de produtos diferentes, e a primeira impressao - que e o que este
 * experimento esta testando - se perde no caminho.
 */

/**
 * A moldura: formulario a esquerda, painel escuro a direita a partir de `lg`.
 *
 * O painel repete a promessa da landing em vez de decorar. Quem chega aqui vindo de
 * la nao deve sentir que trocou de produto.
 */
export function AccountFrame(props: { children: JSX.Element; wide?: boolean }): JSX.Element {
    return (
        <div class={Css.frame}>
            <div class={Css.column}>
                <div class={Css.content} classList={{ [Css.wide]: props.wide }}>
                    <a href="#/" class={Css.brand}>
                        <Brand class={Css.brandIcon} />
                        <span class={Css.brandName}>
                            Mormaii <span class={Css.brandAccent}>Mais Saúde</span>
                        </span>
                    </a>

                    {props.children}
                </div>
            </div>

            <aside class={Css.showcase}>
                <div class={Css.glowTop} />
                <div class={Css.glowBottom} />

                <div class={Css.showcaseText}>
                    <h2 class={Css.showcaseTitle}>A saúde da sua equipe, em números que você entende</h2>
                    <p class={Css.showcaseDetail}>
                        Sono, coração, pressão e atividade de cada pessoa - reunidos, comparáveis e com faixas que a sua
                        equipe de saúde define.
                    </p>
                </div>
            </aside>
        </div>
    )
}

export function Heading(props: { children: JSX.Element; subtitle?: string }): JSX.Element {
    return (
        <div class={Css.headingBlock}>
            <h1 class={Css.title}>{props.children}</h1>
            <Show when={props.subtitle}>
                <p class={Css.subtitle}>{props.subtitle}</p>
            </Show>
        </div>
    )
}

/** O aviso de erro. So existe no DOM quando ha erro - ver o `Show`. */
export function Alert(props: { message?: string }): JSX.Element {
    return (
        <Show when={props.message}>
            {(text) => (
                <p role="alert" class={Css.failure}>
                    {text()}
                </p>
            )}
        </Show>
    )
}

type FieldProps = {
    label: string
    value: string
    onChange: (value: string) => void
    type?: string
    autocomplete?: string
    error?: string
    /** Ocupa as duas colunas numa grade de dois. */
    wide?: boolean
    disabled?: boolean
    hint?: string
}

export function Field(props: FieldProps): JSX.Element {
    return (
        <label class={Css.field} classList={{ [Css.wide]: props.wide }}>
            <span class={Css.label}>{props.label}</span>
            <input
                type={props.type ?? 'text'}
                autocomplete={props.autocomplete}
                value={props.value}
                disabled={props.disabled}
                onInput={(event) => props.onChange(event.currentTarget.value)}
                class={Css.input}
                classList={{ [Css.rejected]: !!props.error }}
            />
            <Show when={props.error ?? props.hint}>
                <span class={Css.hint} classList={{ [Css.rejected]: !!props.error }}>
                    {props.error ?? props.hint}
                </span>
            </Show>
        </label>
    )
}

export function PrimaryButton(props: {
    children: JSX.Element
    type?: 'submit' | 'button'
    busy?: boolean
    onClick?: () => void
}): JSX.Element {
    return (
        <button type={props.type ?? 'submit'} disabled={props.busy} onClick={props.onClick} class={Css.primary}>
            {props.children}
        </button>
    )
}

export function TextButton(props: { children: JSX.Element; onClick: () => void; class?: string }): JSX.Element {
    return (
        <button type="button" onClick={props.onClick} class={`${Css.textButton} ${props.class ?? ''}`}>
            {props.children}
        </button>
    )
}
